import { Tag } from "antd";
import moment from "moment-timezone";
import { ReactNode } from "react";
import {
  REBRAND_AM_DARK,
  REBRAND_NOC_DARK,
  REBRAND_PM_DARK,
} from "@src/appV2/lib/Theme/rebrandConstants";
import { Shift, ShiftName } from "src/interface";
import { FULL_ZONE_NAMES } from "../constants/timezone";
import { getOffsetFormattedTextBetweenTimezones } from "../utils/datetime";
import { getWorkerShiftTypeLabel } from "../appV2/redesign/utils/worker";
import { useGetQualifications } from "@src/appV2/Admin/Qualifications/api/useGetQualifications";
import { isDefined } from "@clipboard-health/util-ts";
import { getQualificationLabel } from "@src/appV2/Admin/Qualifications/utils/getQualificationLabel";
import {
  ORIENTATION_SHIFT_NAME,
  ORIENTATION_SHIFT_GREEN_COLOR,
} from "@src/appV2/OrientationBlock/constants";

const lightBlue = "#f1f8ff";

const amColorRebrand = "#FDCCA6";
const pmColorRebrand = "#A3D8FA";
const nocColorRebrand = "#CCB8FF";
const cancelColor = "grey";
const dailyPayColor = "#ddd622";
const fullPaidColor = "#94C95F";
const partiallyPaidColor = "#1890FF";

function getShiftTagStyle(name: string, working?: boolean, signed = false, margin?: string) {
  const filledTextColor = "#000";

  const style = {
    padding: "0px 4px 0px 4px",
    fontSize: "0.75em",
    margin: margin ?? undefined,
  };

  if (signed) {
    return { ...style };
  }

  switch (name.toLowerCase()) {
    case ShiftName.AM:
      if (working) {
        return {
          ...style,
          backgroundColor: lightBlue,
          color: REBRAND_AM_DARK,
          borderColor: amColorRebrand,
        };
      }
      return {
        ...style,
        backgroundColor: amColorRebrand,
        color: filledTextColor,
        borderColor: amColorRebrand,
      };
    case ShiftName.PM:
      if (working) {
        return {
          ...style,
          backgroundColor: lightBlue,
          color: REBRAND_PM_DARK,
          borderColor: pmColorRebrand,
        };
      }
      return {
        ...style,
        backgroundColor: pmColorRebrand,
        color: filledTextColor,
        borderColor: pmColorRebrand,
      };
    case ShiftName.NOC:
      if (working) {
        return {
          ...style,
          backgroundColor: lightBlue,
          color: REBRAND_NOC_DARK,
          borderColor: nocColorRebrand,
        };
      }
      return {
        ...style,
        backgroundColor: nocColorRebrand,
        color: filledTextColor,
        borderColor: nocColorRebrand,
      };
    default:
      return { backgroundColor: amColorRebrand };
  }
}

export function getShiftTagColor(name: string) {
  switch ((name || "").toLowerCase()) {
    case ShiftName.AM:
      return amColorRebrand;
    case ShiftName.PM:
      return pmColorRebrand;
    case ShiftName.NOC:
      return nocColorRebrand;
    case ORIENTATION_SHIFT_NAME:
      return ORIENTATION_SHIFT_GREEN_COLOR;
    case "cancel":
      return cancelColor;
    case "daily-pay":
      return dailyPayColor;
    case "full paid":
      return fullPaidColor;
    case "partially paid":
      return partiallyPaidColor;
    case "not yet paid":
      return cancelColor;
    default:
      return amColorRebrand;
  }
}

/**
 * FIXME - split this code into separate files - one component per file
 */
interface ShiftTagProps {
  working?: boolean;
  shift: string;
  size?: number;
  text?: string;
  open?: boolean;
  signed?: boolean;
  margin?: string | undefined;
}

export function ShiftTag(props: ShiftTagProps) {
  const { working, shift, size, text, open, signed, margin } = props;
  let body: ReactNode | string = text || shift.toUpperCase();
  if (open) {
    // oxlint-disable-next-line typescript/no-base-to-string
    body += " Open";
    return <Tag style={getShiftTagStyle(shift, working, false, margin)}>{body}</Tag>;
  }

  if (working) {
    body = `Working ${shift.toUpperCase()}`;
    if (size && size <= 1) {
      body = (
        <div>
          <img style={{ height: 15, marginRight: 4 }} src="/bunnyB.png" alt="img" />
          {shift.toUpperCase()}
        </div>
      );
    }
  }

  return (
    <Tag
      style={getShiftTagStyle(shift, working, signed, margin)}
      color={signed ? "gold" : undefined}
      test-id={`shift-slot-${shift}`}
    >
      {body}
    </Tag>
  );
}

/**
 * FIXME - split this code into separate files - one component per file
 */
interface ShiftTitleProps {
  shift: Shift;
  isAgent?: boolean;
  isFacility?: boolean;
  facilityTimezone?: string;
}

export function ShiftTitle(props: ShiftTitleProps) {
  const { shift, isAgent, isFacility, facilityTimezone } = props;
  const { data: qualificationsData } = useGetQualifications(
    {
      filter: {
        workplaceId: shift.facility?.userId,
        enabled: isDefined(shift.facility?.userId),
      },
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 15,
      refetchInterval: 1000 * 60 * 15,
    }
  );

  const qualifications = qualificationsData?.data ?? [];
  const shiftQualificationDisplayLabel = getQualificationLabel(
    shift.agentReq ?? "",
    qualifications
  );

  moment.fn.zoneName = function () {
    const abbr = this.zoneAbbr();
    return FULL_ZONE_NAMES[abbr] || abbr;
  };
  let shiftStart = moment(shift.start);
  let shiftEnd = moment(shift.end);
  const timeFormat = "hh:mm a";
  const { tmz: agentTimezone } = shift.agent || {};
  if (facilityTimezone) {
    shiftStart = moment(shift.start).tz(facilityTimezone);
    shiftEnd = moment(shift.end).tz(facilityTimezone);
  }
  const startAndEndInDifferentTimezone = shiftStart.zoneAbbr() !== shiftEnd.zoneAbbr();
  const rangeText = `${shiftStart.format(`${timeFormat} z`)} until ${shiftEnd.format(
    `${isFacility && startAndEndInDifferentTimezone ? `${timeFormat} z,` : timeFormat}`
  )}`;

  const durationInHours = shiftEnd.diff(shiftStart, "hour", true);
  return (
    <div>
      <ShiftTag shift={shift.name!} />
      <Tag>
        {getWorkerShiftTypeLabel(
          shiftQualificationDisplayLabel ?? shift.agentReq,
          shift.facility?.fullAddress?.state
        )}
      </Tag>
      {shiftStart.calendar(null, {
        sameElse: "ddd MMM Do YYYY",
      })}
      &nbsp;
      {!shift.deleted && (
        <i>
          ({durationInHours?.toFixed(2)} hrs, {rangeText}{" "}
          {shiftEnd.date() !== shiftStart.date() ? "next day" : ""}{" "}
          {isFacility &&
            startAndEndInDifferentTimezone &&
            "with a clock change for Daylight Saving Time"}
          )
        </i>
      )}
      {isAgent &&
        agentTimezone &&
        facilityTimezone &&
        moment(shift.start).tz(agentTimezone).utcOffset() !==
          moment(shift.start).tz(facilityTimezone).utcOffset() &&
        ` - ${moment().tz(facilityTimezone).zoneName()} (${getOffsetFormattedTextBetweenTimezones(
          shift.start,
          agentTimezone,
          facilityTimezone
        )})`}
      {shift.deleted && (
        <i>{shift.isBillable && "(Late Cancel: $" + shift.charge! * shift.time! + " Fee)"}</i>
      )}
    </div>
  );
}
